<script setup lang="ts">
import { useMarkAsRead } from '@/data/Cases/useMarkAsRead'
import { provideCaseSidebarNavigationContext } from '@/modules/Cases/Sidebar/caseSidebarNavigationContext'
import { useCaseSidebarNavigator } from '@/modules/Cases/Sidebar/useCaseSidebarNavigator'
import { useCaseChannel } from '@/modules/Cases/useCaseChannel'
import { provideWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { isFileField } from '@/shared/utils/typeGuards'
import ResizablePanels from '@/sharedComponents/ResizablePanels.vue'
import { computed, onBeforeUnmount, onUnmounted, toRef, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useEntity } from '../Project/useEntity'
import { useGroundingStore } from '../Project/useGroundingStore'
import CaseChat from './CaseChat.vue'
import CaseFileViewer from './CaseFileViewer.vue'
import { useCaseSidebarRoutingToggle } from './Sidebar/useCaseSidebarRoutingToggle'
import { useCaseStore } from './useCaseStore'
import { useCaseUploadStore } from './useCaseUploadStore'

const props = defineProps<{
  caseId: string
  workspaceId: string
  agentId?: string
  hubId?: string
}>()

provideWorkspaceContext(toRef(props, 'workspaceId'))

const caseStore = useCaseStore()
const entityStore = useEntity()
const groundingStore = useGroundingStore()
const uploadStore = useCaseUploadStore()
useCaseChannel(props.workspaceId, toRef(props, 'caseId'))

const router = useRouter()

const caseSidebarNavigator = useCaseSidebarNavigator()
provideCaseSidebarNavigationContext(caseSidebarNavigator)

/** The file (if any) that we render in the file viewer. */
const activeFile = computed(() => {
  if (groundingStore.activeInputField) {
    return groundingStore.activeInputField
  }

  const activeField =
    entityStore.activePropertyId && entityStore.entity?.fields.get(entityStore.activePropertyId)
  if (!activeField || !isFileField(activeField)) {
    return null
  }

  return activeField
})

const { isSidebarOpen } = useCaseSidebarRoutingToggle()

const openAgentInSidebar = async (agentId: string, entityIds: string[]) => {
  if (entityIds.length === 1) {
    caseSidebarNavigator.navigate({ projectId: agentId, entityId: entityIds[0] })
  } else {
    caseSidebarNavigator.navigate({ projectId: agentId })
  }
}

const onCloseFileViewer = () => {
  entityStore.activePropertyId = null
  groundingStore.reset()
}

const onChangeFileField = (propertyId: string) => {
  groundingStore.activeInputId = propertyId
  entityStore.activePropertyId = propertyId
}

const markAsRead = useMarkAsRead()
watch(
  () => props.caseId,
  async (caseId, oldCaseId) => {
    if (oldCaseId) {
      markAsRead.mutate(oldCaseId)
    }

    markAsRead.mutate(caseId)
    // this will set the active case ID and load the case record
    const res = await caseStore.setActiveCase({ caseId, workspaceId: props.workspaceId })
    if (!res.ok) {
      router.push({ name: 'CaseNotFound' })
      return
    }

    // this will add any files not attached to a message to the upload store
    // the UI will treat them as files attached to the next query we send (above the input field)
    uploadStore.addDetachedFiles(caseStore.activeCaseFilesCategorized.detachedUploading, caseId)
    uploadStore.addDetachedFiles(caseStore.activeCaseFilesCategorized.detachedErrored, caseId)
  },
  { immediate: true },
)

watch(
  () => caseStore.latestOutput,
  (newOutput, oldOutput) => {
    // Only navigate if the latest output is a new entity.
    if (!newOutput || newOutput.entityId === oldOutput?.entityId) return
    caseSidebarNavigator.navigate({
      projectId: newOutput.project.id,
      entityId: newOutput.entity.id,
    })
  },
  { immediate: true },
)

watch(
  () => caseStore.activeCaseId,
  () => {
    onCloseFileViewer()
    caseSidebarNavigator.reset()
  },
)

onBeforeUnmount(async () => {
  markAsRead.mutate(props.caseId)
  groundingStore.reset()
})

onUnmounted(() => {
  caseStore.activeCaseId = null
})

const panels = computed(() => [
  { id: 'main', minWidthPx: 350, defaultPercentage: 60, show: true },
  { id: 'sidebar', minWidthPx: 350, defaultPercentage: 40, show: isSidebarOpen.value },
])
</script>

<template>
  <ResizablePanels
    class="bg-surface-secondary"
    storage-key="case-layout"
    :panels="panels"
  >
    <template #main>
      <CaseFileViewer
        v-if="activeFile"
        :field="activeFile"
        :available-file-fields="entityStore.fileFields"
        class="h-full bg-surface-secondary"
        @close="onCloseFileViewer"
        @change:field="onChangeFileField"
      />

      <CaseChat
        v-else
        :key="caseId"
        :case-id="caseId"
        :workspace-id="workspaceId"
        :hub-id="hubId"
        class="h-full"
        @select-agent="openAgentInSidebar"
      />
    </template>
    <template #sidebar>
      <RouterView class="size-full overflow-auto" />
    </template>
  </ResizablePanels>
</template>
